import { X, Minus, Plus, Trash2, ShoppingBasket } from "lucide-react";
import { useState } from "react";
import { useCart } from "@/context/CartContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export function CartDrawer() {
  const { items, open, setOpen, updateQuantity, removeItem, subtotal, checkout, count } = useCart();
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const handleCheckout = async () => {
    setLoading(true);
    try {
      await checkout();
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60]" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-[#2C221E]/40 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="cart-drawer-backdrop" />
      <aside className="absolute right-0 top-0 h-full w-full max-w-md bg-[#FAF5EE] shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#E6D7C3]">
          <h2 className="font-serif-display text-2xl font-bold text-[#2C221E]">Your Basket</h2>
          <button onClick={() => setOpen(false)} data-testid="cart-close-button" className="p-2 rounded-full hover:bg-[#F3EBE0]" aria-label="Close basket">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center gap-3" data-testid="cart-empty-state">
              <ShoppingBasket className="w-12 h-12 text-[#E6D7C3]" />
              <p className="text-[#8C7A6B] text-sm">Your basket is empty.<br />Something sweet is waiting in the shop.</p>
            </div>
          ) : (
            <ul className="flex flex-col gap-5">
              {items.map((item) => (
                <li key={item.id} className="flex gap-4" data-testid={`cart-item-${item.id}`}>
                  <img src={item.image_url} alt={item.name} className="w-20 h-20 rounded-xl object-cover bg-white border border-[#E6D7C3]" />
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <h3 className="font-serif-display font-semibold text-[#2C221E] truncate">{item.name}</h3>
                      <button onClick={() => removeItem(item.id)} data-testid={`cart-remove-${item.id}`} className="text-[#8C7A6B] hover:text-[#9A3412]" aria-label={`Remove ${item.name}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-sm text-[#B45309] font-semibold mt-0.5">${item.price.toFixed(2)}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <button onClick={() => updateQuantity(item.id, -1)} data-testid={`cart-decrease-${item.id}`} className="w-7 h-7 rounded-full border border-[#E6D7C3] flex items-center justify-center hover:bg-[#F3EBE0]" aria-label="Decrease">
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="text-sm font-medium w-4 text-center" data-testid={`cart-qty-${item.id}`}>{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.id, 1)} data-testid={`cart-increase-${item.id}`} className="w-7 h-7 rounded-full border border-[#E6D7C3] flex items-center justify-center hover:bg-[#F3EBE0]" aria-label="Increase">
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[#E6D7C3] px-6 py-5 bg-white">
            <div className="flex justify-between mb-1 text-sm text-[#5A4D44]">
              <span>Subtotal ({count} item{count > 1 ? "s" : ""})</span>
              <span className="font-semibold text-[#2C221E]" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
            </div>
            <p className="text-xs text-[#8C7A6B] mb-4">Shipping included · tax calculated at checkout.</p>
            <button
              onClick={handleCheckout}
              disabled={loading}
              data-testid="cart-checkout-button"
              className="w-full py-3.5 rounded-full bg-[#D97706] hover:bg-[#B45309] text-white font-semibold text-sm transition-colors disabled:opacity-60"
            >
              {loading ? "Buzzing over to checkout..." : "Checkout Securely"}
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}
